import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { RootState, useAppSelector } from "src/store";
import { useKeywordsApi } from "../api";
import { resetError, resetList, setError, setFilter, setList, setLoading } from "../slices";

const getServerSortField = (sort?: string) => {
  if (!sort) return undefined;

  const sortMap: Record<string, string> = {
    dateCreated: "date_created",
  };

  const [field, order] = sort.split("_");
  const serverField = sortMap[field] || field;
  return `${serverField}_${order}`;
};

export const useKeywordsService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const { loading, filter, list, error } = useAppSelector((state) => state.keywords);

  const api = useKeywordsApi();

  const getKeywords = useCallback(async (companyId: string) => {
    const filter = getState().keywords.filter;

    try {
      dispatch(resetError());
      dispatch(setLoading({ getList: true }));

      const response = await api.getKeywords({
        companyId,
        limit: filter.limit,
        offset: filter.offset,

        query: filter.query || undefined,
        sort: getServerSortField(filter.sort),
      });
      dispatch(setList(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getList: false }));
    }
  }, []);

  const changeKeywordsFilter = useCallback((filter: Partial<RootState["keywords"]["filter"]>) => {
    dispatch(setFilter(filter));
  }, []);

  const resetKeywords = useCallback(() => {
    dispatch(resetList());
  }, []);

  return {
    loading,
    filter,
    list,
    error,
    getKeywords,
    changeKeywordsFilter,
    resetKeywords,
  };
};
